import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  UserIcon, 
  BuildingOfficeIcon,
  UsersIcon 
} from '@heroicons/react/24/outline';
import ContactRow from './ContactRow';
import ContactFormModal from './ContactFormModal';
import { contactService } from '../../services/ContactService';
import { auth } from '../../firebase';
import { 
  isToday, 
  isYesterday, 
  isThisWeek, 
  isThisMonth, 
  isSameMonth,
  subMonths,
  format 
} from 'date-fns';
import SearchBar from '../common/SearchBar';
import FilterBar from '../common/FilterBar';

function ContactsList() {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'person', 'organization'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  const filterOptions = [
    { id: 'all', label: t('contacts.all') },
    { id: 'person', label: t('contacts.persons'), icon: UserIcon },
    { id: 'organization', label: t('contacts.organizations'), icon: BuildingOfficeIcon },
  ];

  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = contactService.subscribeToContacts(
      currentUser.uid,
      (data) => {
        setContacts(data);
        setLoading(false);
      },
      { type: typeFilter !== 'all' ? typeFilter : undefined } // Pass filter if not all
    );

    return () => unsubscribe();
  }, [currentUser, typeFilter]);

  // Helper to get date from timestamp
  const getDate = (timestamp) => {
      return timestamp ? timestamp.toDate() : new Date(0);
  };

  const processedContacts = useMemo(() => {
    // 1. Filter
    let filtered = contacts.filter(contact => {
      // Type filter is handled by subscription if not 'all', but we keep client side for robustness
      if (typeFilter !== 'all' && contact.type !== typeFilter) return false;

      // Search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const nameMatch = contact.name?.toLowerCase().includes(term);
        const surnameMatch = contact.surname?.toLowerCase().includes(term);
        const emailMatch = contact.emailAddresses?.some(email => email.toLowerCase().includes(term));
        const orgMatch = contact.relatedAccountName?.toLowerCase().includes(term);
        const notesMatch = contact.notes?.toLowerCase().includes(term);
        
        return nameMatch || surnameMatch || emailMatch || orgMatch || notesMatch;
      }
      return true;
    });

    // 2. Compute lastActivityDate and Sort
    filtered = filtered.map(contact => {
        const lastNote = getDate(contact.lastNoteCreatedDate);
        const lastUpdate = getDate(contact.lastUpdatedDate);
        const creation = getDate(contact.creationDate);
        
        // max of dates
        const lastActivityDate = new Date(Math.max(lastNote.getTime(), lastUpdate.getTime(), creation.getTime()));
        
        return { ...contact, lastActivityDate };
    });

    // Sort desc
    filtered.sort((a, b) => b.lastActivityDate - a.lastActivityDate);

    return filtered;
  }, [contacts, searchTerm, typeFilter]);

  // 3. Group by Date (Logic matching iOS/Notes.js)
  const groupedContacts = useMemo(() => {
      if (searchTerm) {
          // Don't group when searching, just return flat list (or single group)
          // iOS behavior: "Search: ... Pagination disabled during search"
          // Usually search results are flat list.
          return [{ section: t('contacts.searchResults'), contacts: processedContacts }];
      }

      const todayKey = t('contacts.today');
      const yesterdayKey = t('contacts.yesterday');
      const thisWeekKey = t('contacts.thisWeek');
      const lastWeekKey = t('contacts.lastWeek');
      const thisMonthKey = t('contacts.thisMonth');
      const lastMonthKey = t('contacts.lastMonth');
      
      const groups = {
        [todayKey]: [],
        [yesterdayKey]: [],
        [thisWeekKey]: [],
        [lastWeekKey]: [],
        [thisMonthKey]: [],
        [lastMonthKey]: [],
      };
      
      const now = new Date();
      const lastMonthDate = subMonths(now, 1);

      const yearGroups = {};

      processedContacts.forEach(contact => {
          const date = contact.lastActivityDate;
          
          if (isToday(date)) groups[todayKey].push(contact);
          else if (isYesterday(date)) groups[yesterdayKey].push(contact);
          else if (isThisWeek(date)) groups[thisWeekKey].push(contact);
          // "Last week" logic is tricky with date-fns standard helpers, simplified here:
          // Check if it's within last 14 days but not this week
          else if (date > new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)) groups[lastWeekKey].push(contact);
          else if (isThisMonth(date)) groups[thisMonthKey].push(contact);
          else if (isSameMonth(date, lastMonthDate)) groups[lastMonthKey].push(contact);
          else {
              const year = format(date, 'yyyy');
              if (!yearGroups[year]) yearGroups[year] = [];
              yearGroups[year].push(contact);
          }
      });

      const sections = [
          { section: todayKey, contacts: groups[todayKey] },
          { section: yesterdayKey, contacts: groups[yesterdayKey] },
          { section: thisWeekKey, contacts: groups[thisWeekKey] },
          { section: lastWeekKey, contacts: groups[lastWeekKey] },
          { section: thisMonthKey, contacts: groups[thisMonthKey] },
          { section: lastMonthKey, contacts: groups[lastMonthKey] },
      ];

      // Add years desc
      Object.keys(yearGroups).sort((a, b) => b - a).forEach(year => {
          sections.push({ section: year, contacts: yearGroups[year] });
      });

      return sections.filter(s => s.contacts.length > 0);
  }, [processedContacts, searchTerm, t]);


  const handleCreateClick = () => {
    setContactToEdit(null);
    setIsModalOpen(true);
  };

  const handleCardClick = (contact) => {
    navigate(`/contacts/${contact.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">{t('contacts.loadingContacts')}</div>
      </div>
    );
  }

  return (
    <div className="p-1 md:px-2 md:pb-4 md:pt-0 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pt-4 md:pt-0">
        <div className="hidden md:flex items-center gap-x-3">
          <UsersIcon className="h-8 w-8 text-gray-900" />
          <h1 className="text-3xl font-bold text-gray-900">{t('contacts.contacts')}</h1>
        </div>
        
        <div className="w-full md:w-auto md:ml-auto flex items-center gap-2 md:gap-6">
          {/* Search */}
          <div className="relative flex-1 md:flex-none md:w-48">
            <SearchBar 
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t('contacts.searchContacts')}
            />
          </div>

          <button
            onClick={handleCreateClick}
            className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-500 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 whitespace-nowrap"
          >
            {t('contacts.add')}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar 
        options={filterOptions}
        selectedFilter={typeFilter}
        onFilterChange={setTypeFilter}
        className="mt-4"
      />

      {/* Contacts List with Sections */}
      <div className="overflow-y-auto pb-20">
          {groupedContacts.length > 0 ? (
              groupedContacts.map((group) => (
                  <div key={group.section} className="mb-6">
                      {/* Section Header (skip if search or single default group) */}
                      {!searchTerm && (
                          <h3 className="text-xl md:text-lg font-bold text-gray-700 mb-3 px-1">{group.section}</h3>
                      )}
                      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 shadow-sm overflow-hidden">
                          {group.contacts.map(contact => (
                              <ContactRow
                                  key={contact.id}
                                  contact={contact}
                                  onClick={handleCardClick}
                              />
                          ))}
                      </div>
                  </div>
              ))
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <UsersIcon className="h-12 w-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">{t('contacts.noContactsFound')}</p>
              <p className="text-sm">
                {searchTerm || typeFilter !== 'all' 
                  ? t('contacts.tryAdjustingSearch') 
                  : t('contacts.getStartedCreatingContact')}
              </p>
            </div>
          )}
      </div>

      {/* Modal */}
      {currentUser && (
        <ContactFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          contactToEdit={contactToEdit}
          uid={currentUser.uid}
        />
      )}
    </div>
  );
}

export default ContactsList;
